import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Route as RouteIcon, Plus, Calendar, MapPin, Users as UsersIcon, Search, Trash2,
  Filter, X, Truck,
} from "lucide-react";
import LearnerHint from "@/components/LearnerHint";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const ESTADOS_RUTA = {
  planificada: { label: "Planificada", color: "bg-amber-500/20 text-amber-300" },
  en_curso:    { label: "En curso",    color: "bg-blue-500/20 text-blue-300" },
  finalizada:  { label: "Finalizada",  color: "bg-emerald-500/20 text-emerald-300" },
  cancelada:   { label: "Cancelada",   color: "bg-zinc-500/20 text-zinc-400" },
};

const ATAJOS_FECHA = [
  { id: "hoy",       label: "Hoy" },
  { id: "semana",    label: "Esta semana" },
  { id: "mes",       label: "Este mes" },
  { id: "proximos7", label: "Próximos 7d" },
];

function fechasAtajo(id) {
  const hoy = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  if (id === "hoy") return { desde: iso(hoy), hasta: iso(hoy) };
  if (id === "semana") {
    const dow = (hoy.getDay() + 6) % 7; // lunes = 0
    const lunes = new Date(hoy); lunes.setDate(hoy.getDate() - dow);
    const dom = new Date(lunes); dom.setDate(lunes.getDate() + 6);
    return { desde: iso(lunes), hasta: iso(dom) };
  }
  if (id === "mes") {
    const primer = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
    const ultimo = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
    return { desde: iso(primer), hasta: iso(ultimo) };
  }
  if (id === "proximos7") {
    const fin = new Date(hoy); fin.setDate(hoy.getDate() + 7);
    return { desde: iso(hoy), hasta: iso(fin) };
  }
  return { desde: "", hasta: "" };
}

export default function RutasList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [fEstado, setFEstado] = useState("todos");
  const [fDesde, setFDesde] = useState("");
  const [fHasta, setFHasta] = useState("");
  const [fMontador, setFMontador] = useState("todos");
  const [fVehiculo, setFVehiculo] = useState("todos");
  const [montadores, setMontadores] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [filtrosAbierto, setFiltrosAbierto] = useState(false);
  const { hasRole } = useAuth();
  const isAdmin = hasRole && hasRole("admin");

  // Cargar opciones de montador y vehículo (1 sola vez)
  useEffect(() => {
    (async () => {
      try {
        const [u, v] = await Promise.all([
          api.get("/users"),
          api.get("/vehiculos"),
        ]);
        // Filtrar solo activos con rol montador o admin que pueda salir
        const mons = (u.data || []).filter(
          (x) => x.active !== false && (x.roles || []).some((r) => r === "montador" || r === "montador_externo" || r === "admin"),
        );
        setMontadores(mons);
        setVehiculos((v.data || []).filter((x) => x.activo !== false));
      } catch {
        // silencioso, los filtros opcionales no son críticos
      }
    })();
  }, []);

  // Construir params con filtros
  const params = useMemo(() => {
    const p = {};
    if (fEstado !== "todos") p.estado = fEstado;
    if (fDesde) p.fecha_desde = fDesde;
    if (fHasta) p.fecha_hasta = fHasta;
    if (fMontador !== "todos") p.montador_id = fMontador;
    if (fVehiculo !== "todos") p.vehiculo_id = fVehiculo;
    if (q.trim()) p.q = q.trim();
    return p;
  }, [fEstado, fDesde, fHasta, fMontador, fVehiculo, q]);

  // Cargar rutas con debounce de q
  useEffect(() => {
    const h = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/rutas", { params });
        setItems(data || []);
      } catch (e) {
        toast.error("Error cargando rutas");
      } finally { setLoading(false); }
    }, q ? 300 : 0); // sólo retraso si hay texto
    return () => clearTimeout(h);
  }, [params, q]);

  const eliminar = async (e, r) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`¿Eliminar la ruta ${r.codigo} · ${r.nombre || ""}?\n\nEsta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/rutas/${r.id}`);
      toast.success("Ruta eliminada");
      const { data } = await api.get("/rutas", { params });
      setItems(data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar");
    }
  };

  const limpiarFiltros = () => {
    setQ(""); setFEstado("todos"); setFDesde(""); setFHasta("");
    setFMontador("todos"); setFVehiculo("todos");
  };

  // Contador de filtros activos (excluyendo el search box)
  const filtrosActivos = (
    (fEstado !== "todos" ? 1 : 0)
    + (fDesde ? 1 : 0)
    + (fHasta ? 1 : 0)
    + (fMontador !== "todos" ? 1 : 0)
    + (fVehiculo !== "todos" ? 1 : 0)
  );

  return (
    <div className="p-8 max-w-[1800px]">
      <PageHeader
        title="Rutas de montaje"
        subtitle="Crea rutas asignando trabajos listos, montadores y vehículos"
        icon={RouteIcon}
        action={
          <Link to="/rutas/nueva">
            <LearnerHint label="Crear ruta asignando trabajos a montadores">
              <Button data-testid="new-ruta-btn" className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
                <Plus className="w-4 h-4 mr-1" /> Nueva ruta
              </Button>
            </LearnerHint>
          </Link>
        }
      />

      {/* Iter 89 — Barra de búsqueda + estado + toggle filtros avanzados */}
      <div className="flex gap-3 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por código, nombre, observaciones…"
            data-testid="rutas-search"
            className="pl-8 bg-zinc-900 border-zinc-800 h-9 text-sm" />
        </div>
        <Select value={fEstado} onValueChange={setFEstado}>
          <SelectTrigger data-testid="rutas-filter-estado" className="w-44 bg-zinc-900 border-zinc-800 h-9 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
            <SelectItem value="todos">Todos los estados</SelectItem>
            {Object.entries(ESTADOS_RUTA).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setFiltrosAbierto((v) => !v)}
          data-testid="rutas-filtros-toggle"
          className={`h-9 text-xs border-zinc-800 ${filtrosActivos > 0 ? "bg-amber-500/10 border-amber-500/40 text-amber-200" : "bg-zinc-900 text-zinc-300"}`}
        >
          <Filter className="w-3.5 h-3.5 mr-1" />
          Filtros{filtrosActivos > 0 ? ` · ${filtrosActivos}` : ""}
        </Button>
        {(filtrosActivos > 0 || q) && (
          <Button variant="ghost" size="sm" onClick={limpiarFiltros}
                  data-testid="rutas-filtros-clear"
                  className="h-9 text-xs text-zinc-400 hover:text-zinc-100">
            <X className="w-3 h-3 mr-1" /> Limpiar
          </Button>
        )}
        <div className="text-xs text-zinc-500 self-center ml-auto">
          {loading ? "Cargando…" : `${items.length} ruta${items.length === 1 ? "" : "s"}`}
        </div>
      </div>

      {/* Panel de filtros avanzados (colapsable) */}
      {filtrosAbierto && (
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4 mb-4 space-y-3"
             data-testid="rutas-filtros-panel">
          {/* Atajos de fecha */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider text-zinc-500">Atajos:</span>
            {ATAJOS_FECHA.map((a) => (
              <Button key={a.id} size="sm" variant="outline"
                onClick={() => { const r = fechasAtajo(a.id); setFDesde(r.desde); setFHasta(r.hasta); }}
                data-testid={`rutas-atajo-${a.id}`}
                className="h-7 px-2 text-[11px] bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-amber-500/40">
                {a.label}
              </Button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Desde</label>
              <Input type="date" value={fDesde} onChange={(e) => setFDesde(e.target.value)}
                data-testid="rutas-fecha-desde"
                className="bg-zinc-900 border-zinc-800 h-9 text-xs" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Hasta</label>
              <Input type="date" value={fHasta} onChange={(e) => setFHasta(e.target.value)}
                data-testid="rutas-fecha-hasta"
                className="bg-zinc-900 border-zinc-800 h-9 text-xs" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block flex items-center gap-1">
                <UsersIcon className="w-3 h-3" /> Montador
              </label>
              <Select value={fMontador} onValueChange={setFMontador}>
                <SelectTrigger data-testid="rutas-filter-montador" className="bg-zinc-900 border-zinc-800 h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-72">
                  <SelectItem value="todos">Todos los montadores</SelectItem>
                  {montadores.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name || m.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block flex items-center gap-1">
                <Truck className="w-3 h-3" /> Vehículo
              </label>
              <Select value={fVehiculo} onValueChange={setFVehiculo}>
                <SelectTrigger data-testid="rutas-filter-vehiculo" className="bg-zinc-900 border-zinc-800 h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-72">
                  <SelectItem value="todos">Todos los vehículos</SelectItem>
                  {vehiculos.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.matricula} {v.modelo ? `· ${v.modelo}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      {items.length === 0 && !loading && (
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-10 text-center text-zinc-400"
             data-testid="rutas-empty">
          {(q || filtrosActivos > 0)
            ? "Sin coincidencias para los filtros actuales."
            : "Aún no hay rutas creadas."}
        </div>
      )}

      <div className="space-y-2" data-testid="rutas-list">
        {items.map((r) => {
          const est = ESTADOS_RUTA[r.estado] || ESTADOS_RUTA.planificada;
          return (
            <Link to={`/rutas/${r.id}`} key={r.id} data-testid={`ruta-row-${r.codigo}`}
              className="block bg-zinc-900/40 border border-zinc-800 hover:border-amber-500/40 rounded-xl p-4 transition-colors">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-xs text-zinc-500 font-mono">{r.codigo}</span>
                <span className="text-base font-semibold text-zinc-100">{r.nombre}</span>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${est.color}`}>{est.label}</span>
                <span className="text-xs text-zinc-400 flex items-center gap-1"><Calendar className="w-3 h-3" /> {r.fecha}</span>
                {r.provincia_principal && (
                  <span className="text-xs text-zinc-400 flex items-center gap-1"><MapPin className="w-3 h-3" /> {r.provincia_principal}{r.provincia_secundaria ? ` / ${r.provincia_secundaria}` : ""}</span>
                )}
                <span className="text-xs text-zinc-400 flex items-center gap-1 ml-auto">
                  <UsersIcon className="w-3 h-3" /> {r.montadores?.length || 0} · 📍 {r.paradas?.length || 0}
                </span>
                {isAdmin && (
                  <button
                    type="button"
                    onClick={(e) => eliminar(e, r)}
                    title="Eliminar ruta (admin)"
                    data-testid={`ruta-delete-${r.codigo}`}
                    className="text-zinc-600 hover:text-red-400 p-1 rounded hover:bg-red-500/10 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              {r.observaciones && <div className="text-xs text-zinc-500 mt-1 truncate">{r.observaciones}</div>}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
